import { Link, useNavigate } from "react-router-dom";
import { Fragment } from "react";
import { User, LogOut } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/store/authStore";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { MygrowLogo } from "@/components/brand/MygrowLogo";
import { useTranslation } from "react-i18next";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface ShellBarProps {
  breadcrumbs?: BreadcrumbItem[];
  actions?: React.ReactNode;
  showSidebarTrigger?: boolean;
}

export function ShellBar({ breadcrumbs, actions, showSidebarTrigger = true }: ShellBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const handleLogout = () => {
    trackEvent("logout_clicked");
    logout();
    navigate("/login");
  };

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "??";

  return (
    <header className="h-12 bg-[#08254c] text-white flex items-center px-5 gap-3 shrink-0 z-50 border-b border-white/[0.06]">
      {showSidebarTrigger && (
        <SidebarTrigger className="text-white hover:bg-white/10" />
      )}

      <MygrowLogo className="h-[30px] w-auto shrink-0 text-white" />

      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-2 text-xs ml-3 pl-3 border-l border-white/10 min-w-0"
        >
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <Fragment key={`${crumb.label}-${i}`}>
                {crumb.to && !isLast ? (
                  <Link to={crumb.to} className="text-white/55 hover:text-white truncate max-w-[200px]">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className={isLast ? "text-white font-medium truncate max-w-[260px]" : "text-white/55 truncate max-w-[200px]"}>
                    {crumb.label}
                  </span>
                )}
                {!isLast && <span className="text-white/25 shrink-0">/</span>}
              </Fragment>
            );
          })}
        </nav>
      )}

      <div className="flex-1" />

      {actions}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="h-[30px] w-[30px] rounded-full flex items-center justify-center text-[11px] font-bold font-brand text-[#08254c] cursor-pointer hover:ring-2 hover:ring-white/30 transition-all"
            style={{ background: "linear-gradient(135deg, #ffc83d, #ffb300)" }}
          >
            {initials}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48" sideOffset={6}>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col gap-0.5">
              <p className="text-sm font-medium">{user?.name || "User"}</p>
              <p className="text-xs text-muted-foreground">{user?.email || ""}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/profile")}>
            <User className="mr-2 h-3.5 w-3.5" />
            {t("Profile")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="cursor-pointer text-destructive focus:text-destructive" onClick={handleLogout}>
            <LogOut className="mr-2 h-3.5 w-3.5" />
            {t("Log out")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
